import {
  EmptyState,
  InputSelectionListProps,
  SelectionList,
  Tag,
  Text,
  TextWithHighlight,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import type { Tag as TagT } from 'lib-shared-types-api';
import { useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { Waypoint } from 'react-waypoint';

import { tagsApi } from 'apis/tags';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import styles from './hookFormTagSelect.module.scss';

type HookFormTagSelectProps = Partial<
  Omit<InputSelectionListProps<TagT>, 'children'>
>;

export const HookFormTagSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  componentProps = {},
  ...listProps
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  HookFormTagSelectProps
>): JSX.Element => {
  const [filter, setFilter] = useState('');

  const {
    items: tags,
    asyncStatus,
    changeArg,
    nextPage,
    hasNextPage,
  } = usePaginatedEndpoint({
    endpoint: tagsApi.endpoints.readTags,
    itemsSelector: ({ data: { tags } }) => tags || ([] as TagT[]),
    infinite: true,
    initialArg: {
      // The tag api uses a format for sorting that is incompatible with usePaginatedEndpoint
      // so we leave it out and use the default
      limit: 50,
      offset: 0,
    },
  });

  const hasNoTags = !tags.length;

  return (
    <HookFormSelectionList
      {...listProps}
      componentProps={{
        multiple: true,
        placeholder: 'Select tags',
        equalityFn: (a: TagT, b: TagT) => a.id === b.id,
        getItemTitleByValue: (tag: TagT | null | undefined) =>
          tag ? (
            <Tag color={tag.color} noBorder className={styles.tag}>
              {tag.name}
            </Tag>
          ) : (
            <Text.Paragraph>Unknown</Text.Paragraph>
          ),
        filter: {
          value: filter,
          onChange: (event) => {
            const searchTerm = event.target.value;

            setFilter(searchTerm);
            changeArg(
              { searchTerm, offset: 0 },
              {
                debounceTime: 300,
              },
            );
          },
        },
        onToggleDropdown: (isOpen: boolean) => {
          // When the user closes the dropdown with a previous
          // search term, we reset the search term.
          if (!isOpen && filter !== '') {
            setFilter('');
            changeArg({ searchTerm: '', offset: 0 });
          }
        },
        ...componentProps,
      }}
    >
      <>
        {tags.map((tag) => (
          <SelectionList.Item key={tag.id} value={tag}>
            <Tag color={tag.color} noBorder className={styles.tag}>
              {filter ? (
                <TextWithHighlight text={tag.name} highlight={filter} />
              ) : (
                tag.name
              )}
            </Tag>
          </SelectionList.Item>
        ))}

        {asyncStatus === 'fulfilled' && hasNextPage && (
          <Waypoint onEnter={nextPage} />
        )}

        {hasNoTags && (
          <SelectionList.Item noFixedSelectionStateSpace>
            <EmptyState
              title="No items found"
              description="Try changing your search criteria."
            />
          </SelectionList.Item>
        )}
      </>
    </HookFormSelectionList>
  );
};
